<template>
  <div v-show="show">
    <div
      class="relative z-[100]"
      aria-labelledby="slide-over-title"
      role="dialog"
      aria-modal="true"
    >
      <!-- Background backdrop, show/hide based on slide-over state. -->
      <div class="fixed inset-0"></div>

      <div class="fixed inset-0 overflow-hidden">
        <div class="absolute inset-0 overflow-hidden">
          <div
            class="pointer-events-none fixed inset-y-0 left-0 flex max-w-full pr-10 sm:pr-16"
          >
            <!--
          Slide-over panel, show/hide based on slide-over state.

          Entering: "transform transition ease-in-out duration-500 sm:duration-700"
            From: "translate-x-full"
            To: "translate-x-0"
          Leaving: "transform transition ease-in-out duration-500 sm:duration-700"
            From: "translate-x-0"
            To: "translate-x-full"
        -->
            <transition
              enter-active-class="transform transition ease-in-out duration-500 sm:duration-700"
              enter-class="-translate-x-full"
              enter-to-class="translate-x-0"
              leave-active-class="transform transition ease-in-out duration-500 sm:duration-700"
              leave-class="translate-x-0"
              leave-to-class="-translate-x-full"
            >
              <div v-show="show" class="pointer-events-auto w-screen max-w-md">
                <div
                  class="flex h-full flex-col overflow-y-hidden bg-white shadow-xl"
                >
                  <div class="px-5 py-3 bg-gray-100">
                    <div class="flex items-center justify-between">
                      <h2
                        class="text-sm font-semibold leading-6 text-gray-900"
                        id="slide-over-title"
                      >
                        Gespeicherte Werteräume
                      </h2>
                      <div class="ml-3 flex h-7 items-center">
                        <button
                          @click="cancel"
                          type="button"
                          class="relative rounded-md bg-transparent text-gray-400 focus:ring-2 hover:text-markencode-blue focus:ring-markencode-blue"
                        >
                          <span class="absolute -inset-2.5"></span>
                          <span class="sr-only">Close panel</span>
                          <svg
                            class="h-6 w-6"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke-width="1.5"
                            stroke="currentColor"
                            aria-hidden="true"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>

                  <div v-show="loading" class="mt-2 h-16 relative">
                    <spinner
                      :busy="loading"
                      color="#000"
                      class="mt-0"
                    ></spinner>
                  </div>

                  <div
                    v-if="!loading && !valueSpaces.length"
                    class="text-sm mt-3 px-4"
                  >
                    Es liegen keine gespeicherten Werteräume vor.
                  </div>

                  <ul
                    v-if="valueSpaces.length > 0"
                    role="list"
                    class="flex-1 divide-y divide-gray-200 overflow-y-auto overscroll-none scrollbar-thin scrollbar-track-gray-100, scrollbar-thumb-gray-200"
                  >
                    <template v-for="(valueSpace, idx) in valueSpaces">
                      <li :key="idx">
                        <div
                          class="group relative flex items-center px-2.5 py-2.5"
                        >
                          <a
                            href="#"
                            @click.prevent="select(valueSpace)"
                            class="-m-1 block flex-1 p-1"
                          >
                            <div
                              class="absolute inset-0 group-hover:bg-gray-50"
                              aria-hidden="true"
                            ></div>
                            <div
                              class="relative flex min-w-0 flex-1 items-center"
                            >
                              <div class="ml-4 truncate">
                                <p
                                  class="truncate text-sm font-medium text-gray-900"
                                >
                                  {{ valueSpace.name }}
                                </p>

                                <div
                                  class="mt-2 px-0 py-0 flex flex-wrap items-center gap-x-1 gap-y-1"
                                >
                                  <div
                                    v-for="(word, idx) in valueSpace.words"
                                    :key="idx"
                                    class="inline-flex items-center justify-between gap-x-1 rounded-md border border-gray-400 px-2 py-1 text-xs"
                                  >
                                    <span>{{ word.word }}</span>

                                    <zone-color-icons
                                      :colors="word.zone_colors"
                                      :mini="true"
                                    ></zone-color-icons>
                                  </div>
                                </div>

                                <!-- <p class="truncate text-sm text-gray-500">
                                  {{ words(valueSpace) }}
                                </p> -->
                              </div>
                            </div>
                          </a>
                          <div
                            class="relative ml-2 inline-block flex-shrink-0 text-left"
                          >
                            <button
                              @click="remove(valueSpace)"
                              type="button"
                              class="group relative inline-flex h-8 w-8 items-center justify-center rounded-full bg-white focus:outline-none"
                              aria-expanded="false"
                              aria-haspopup="true"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke-width="1.5"
                                stroke="currentColor"
                                class="z-50 mt-1 mx-auto w-4 h-4 text-gray-400 cursor-pointer hover:text-markencode-blue"
                              >
                                <path
                                  stroke-linecap="round"
                                  stroke-linejoin="round"
                                  d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
                                />
                              </svg>
                            </button>
                          </div>
                        </div>
                      </li>
                    </template>
                  </ul>
                </div>
              </div>
            </transition>
          </div>
        </div>
      </div>
    </div>

    <confirm-modal
      v-if="confirmModal.visible"
      :headline="confirmModal.headline"
      :text="confirmModal.text"
      @close="confirmModal.visible = false"
      @confirm="removeConfirmed"
      class="z-[200]"
    ></confirm-modal>
  </div>

  <!-- value space select: end -->
</template>

<script>
import Spinner from '@/Shared/Spinner';
import ConfirmModal from '@/Shared/ConfirmModal.vue';
import ZoneColorIcons from '@/Pages/Markencode/Dictionary/Shared/ZoneColorIcons.vue';
import ClickOutside from 'vue-click-outside';

export default {
  directives: {
    ClickOutside,
  },

  components: {
    Spinner,
    ConfirmModal,
    ZoneColorIcons,
  },

  props: {
    show: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      confirmModal: {
        visible: false,
        itemId: null,
        headline: 'Werteraum löschen',
        text: '',
      },
      loading: true,
      valueSpaces: [],
    };
  },

  mounted() {
    // this.load();
  },

  watch: {
    show: function () {
      if (this.show) {
        this.$nextTick(() => {
          this.load();
        });
      } else {
        this.$nextTick(() => {
          this.reset();
        });
      }
    },
  },

  methods: {
    load() {
      this.loading = true;

      window.axios
        .post(this.route('markencode.dictionary.value-space.get'))
        .then((response) => {
          // console.log(response.data.value_spaces);
          this.valueSpaces = response.data.value_spaces;
          this.loading = false;
        })
        .catch(function (error) {
          console.log(error);
          this.loading = false;
        });
    },

    reset() {
      this.valueSpaces = [];
    },

    cancel() {
      this.$emit('cancelled');
    },

    // words(valueSpace) {
    //   // return valueSpace.words.join(', ');
    //   // return pluck(valueSpace.words, 'word').join(', ');
    //   return valueSpace.words.map((w) => w.word).join(', ');
    // },

    select(valueSpace) {
      this.$emit('selected', valueSpace.words);
      // this.cancel();
    },

    remove(valueSpace) {
      this.confirmModal.itemId = valueSpace.id;
      this.confirmModal.text = `Soll der Werteraum ${valueSpace.name} wirklich gelöscht werden?`;
      this.confirmModal.visible = true;
    },

    removeConfirmed() {
      if (!this.confirmModal.itemId) return;

      window.axios
        .delete(
          this.route('markencode.dictionary.value-space.delete', {
            id: this.confirmModal.itemId,
          })
        )
        .then((response) => {
          this.valueSpaces = response.data.value_spaces;
          this.confirmModal.visible = false;
          // console.log(response.data.value_spaces);
        })
        .catch(function (error) {
          console.log(error);
          this.confirmModal.visible = false;
        });
    },
  },
};
</script>
